import {
  Button,
  Checkbox,
  Divider,
  Heading,
  HStack,
  Popover,
  PopoverBody,
  PopoverCloseButton,
  PopoverContent,
  PopoverTrigger,
  Portal,
  Spacer,
  Stack,
  useDisclosure,
} from '@chakra-ui/react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useQuery } from 'urql';
import { faShare } from '@fortawesome/free-solid-svg-icons';
import { useExportState } from '../common/ExportStateManagerContext';
import { GetProjectDeepDocument } from '../../gql/graphql';
import SparkelIcon from '../common/icon/SparkelIcon';
import { useBreadcrumb } from '../common/BreadcrumbProvider';

type ProtanTableExporterProps = {
  exportType: 'webshop' | 'prokalk';
};

export const ProtanTableExporter = ({
  exportType,
}: ProtanTableExporterProps) => {
  const { triggerExport, isExporting } = useExportState();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const { projectId } = useBreadcrumb();
  // Query project to list aggregated orders (roofs)
  const [{ data: projectData }] = useQuery({
    query: GetProjectDeepDocument,
    variables: { id: projectId as string },
    pause: !projectId,
  });

  const aggregatedOrders = useMemo(
    () =>
      (projectData?.project?.orders || []).filter(
        (o) => o.isAggregatedOrder === true
      ),
    [projectData?.project?.orders]
  );

  const roofNames = useMemo(() => {
    return aggregatedOrders
      .map((o) => o.name)
      .filter(Boolean)
      .sort() as string[];
  }, [aggregatedOrders]);

  const [selectedRoofs, setSelectedRoofs] = useState<Set<string>>(new Set());

  // Initialize to select all when list changes
  useEffect(() => {
    setSelectedRoofs(new Set(roofNames));
  }, [roofNames]);

  const handleExport = useCallback(
    (names: string[]) => {
      // Orders are resolved in ExportProjectData; we pass only selected roofs
      triggerExport([], ['SKU', 'Quantity'], exportType, names);
    },
    [exportType, triggerExport]
  );

  const allSelected =
    selectedRoofs.size === roofNames.length && roofNames.length > 0;
  const isIndeterminate =
    selectedRoofs.size > 0 && selectedRoofs.size < roofNames.length;

  const toggleSelectAll = () => {
    if (allSelected) {
      setSelectedRoofs(new Set());
    } else {
      setSelectedRoofs(new Set(roofNames));
    }
  };

  const toggleOne = (name: string) => {
    setSelectedRoofs((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name);
      else next.add(name);
      return next;
    });
  };

  if (exportType === 'prokalk') {
    const hasSingleRoof = roofNames.length === 1;

    if (hasSingleRoof) {
      return (
        <Button
          isLoading={isExporting}
          leftIcon={<SparkelIcon icon={faShare} color={'inherit'} />}
          colorScheme="blue"
          borderRadius={'md'}
          onClick={() => handleExport(roofNames)}
        >
          Export to ProKalk
        </Button>
      );
    }

    return (
      <Popover
        isOpen={isOpen}
        onOpen={onOpen}
        onClose={onClose}
        placement="bottom-start"
        isLazy
      >
        <PopoverTrigger>
          <Button
            isLoading={isExporting}
            leftIcon={<SparkelIcon icon={faShare} color={'inherit'} />}
            colorScheme="blue"
            onClick={onOpen}
            borderRadius={'md'}
            isDisabled={roofNames.length === 0}
          >
            Export to ProKalk
          </Button>
        </PopoverTrigger>
        <Portal>
          <PopoverContent boxShadow={'lg'} width={'2xs'}>
            <PopoverCloseButton />
            <PopoverBody>
              <Heading size="sm" marginBottom={4}>
                Select roofs
              </Heading>
              <Stack spacing={2}>
                <Checkbox
                  isChecked={allSelected}
                  isIndeterminate={isIndeterminate}
                  onChange={toggleSelectAll}
                  color={'gray.500'}
                >
                  Select all roofs
                </Checkbox>
                <Divider />
                <Stack spacing={1}>
                  {roofNames.map((name) => (
                    <Checkbox
                      key={name}
                      isChecked={selectedRoofs.has(name)}
                      onChange={() => toggleOne(name)}
                    >
                      {name}
                    </Checkbox>
                  ))}
                </Stack>
              </Stack>
              <HStack>
                <Spacer />
                <Button
                  size={'sm'}
                  borderRadius={'md'}
                  variant={'outline'}
                  leftIcon={<SparkelIcon icon={faShare} color={'inherit'} />}
                  isDisabled={selectedRoofs.size === 0}
                  onClick={() => {
                    // For now, we keep existing export behavior
                    handleExport(Array.from(selectedRoofs));
                    onClose();
                  }}
                >
                  Export
                </Button>
              </HStack>
            </PopoverBody>
          </PopoverContent>
        </Portal>
      </Popover>
    );
  }

  return (
    <Button
      isLoading={isExporting}
      onClick={() => handleExport([])}
      leftIcon={<SparkelIcon icon={faShare} color={'inherit'} />}
      colorScheme="blue"
    >
      {exportType === 'webshop'
        ? 'Export to Protan Webshop'
        : 'Export to ProKalk'}
    </Button>
  );
};
